import { sha256 } from '@noble/hashes/sha256'
import { base58xrp } from '@scure/base'
import { z } from 'zod'

/** Classic Address の version、160bit AccountID、二重 SHA256 checksum を検証する。 */
export const xrplClassicAddressSchema = z
  .string()
  .superRefine((address, ctx) => {
    try {
      const bytes = base58xrp.decode(address)
      if (
        bytes.length !== 25 ||
        bytes[0] !== 0 ||
        base58xrp.encode(bytes) !== address
      )
        throw new Error('Invalid account address')
      const digest = sha256(sha256(bytes.slice(0, 21)))
      for (let index = 0; index < 4; index++) {
        if (bytes[21 + index] !== digest[index])
          throw new Error('Invalid checksum')
      }
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '有効な XRPL Classic Address（r...）を入力してください',
      })
    }
  })

/** XRPL transaction hash の正規化された表記。 */
export const xrplHashSchema = z
  .string()
  .regex(/^[A-Fa-f0-9]{64}$/)
  .transform((value) => value.toUpperCase())

/**
 * Mainnet transaction の決定的な Source ID を作る。
 * @param params transaction hash
 * @returns 同一 transaction で不変の Source ID
 */
export const createXrplSourceId = (params: { hash: string }): string =>
  `xrpl:${xrplHashSchema.parse(params.hash)}`

/**
 * ledger entry に埋め込まれた 160bit AccountID を Classic Address に変換する。
 * @param params 40桁の AccountID hexadecimal
 * @returns checksum 付き Classic Address
 */
export const createXrplAddressFromAccountId = (params: {
  accountId: string
}): string => {
  const accountId = z
    .string()
    .regex(/^[A-Fa-f0-9]{40}$/)
    .parse(params.accountId)
  const payload = new Uint8Array(21)
  for (let index = 0; index < 20; index++)
    payload[index + 1] = Number.parseInt(
      accountId.slice(index * 2, index * 2 + 2),
      16,
    )
  const encoded = new Uint8Array(25)
  encoded.set(payload)
  encoded.set(sha256(sha256(payload)).slice(0, 4), 21)
  return xrplClassicAddressSchema.parse(base58xrp.encode(encoded))
}
